import { Separator, VStack } from '@chakra-ui/react'
import { FaEllo, FaIdCard, FaNetworkWired } from 'react-icons/fa'

import { useUserMenuContextSwitchState } from '@/components/molecules/Menu/UserMenuContent/hooks/useUserMenuContextSwitchState'
import { ContextSwitchSection } from '@/components/molecules/Menu/UserMenuContent/sections/ContextSwitchSection'
import { UserMenuDrawerPrimaryNav } from '@/components/molecules/Menu/UserMenuContent/sections/UserMenuDrawerPrimaryNav'
import { UserMenuDropdownPrimaryNav } from '@/components/molecules/Menu/UserMenuContent/sections/UserMenuDropdownPrimaryNav'
import type { NavEntry } from '@/components/navigation/nav.types'
import { useContextRail } from '@/components/navigation/useContextRail'
import { MenuSeparator } from '@/components/ui/menu'
import { useContextualPath } from '@/hooks/useContextualPath'
import {
  CommunitiesRoutes,
  NetworksRoutes,
  UserRoutes,
} from '@/routes/_hooks/routes.enum'
import { generatePath } from '@/routing'

import type { UserMenuContentProps } from './UserMenuContent.types'
export type { UserMenuContentProps } from './UserMenuContent.types'

export const UserMenuContent = ({
  asDrawer,
  onClose,
  user,
  circles,
  networks,
  activeCircle,
  activeNetwork,
  isCircle,
  isNetwork,
  contextName: _contextName,
  contextIri,
  loading,
  circlesLoading,
  hoveredCircleId,
  setHoveredCircleId,
  endImpersonation,
  isImpersonating,
  captureEvent,
  t,
  handleSwitchContext,
}: UserMenuContentProps) => {
  const rowPy = asDrawer ? 3 : 2
  const rowPx = 4
  const rowGap = 2
  const { generateContextualPath } = useContextualPath()
  // The drawer shows the rail itself; only the desktop dropdown still needs a single door.
  const rail = useContextRail()

  const contextualMaterialsPath = generateContextualPath('materials')
  const contextualBookingsPath = generateContextualPath('bookingsAsOwner')
  const contextualNetworksPath = generateContextualPath('networks')

  // Level-1 door for the active context — mirrors the primary rail entry. The
  // section sub-nav lives inside each space (horizontal nav), not here.
  const contextNavItems: NavEntry[] =
    isNetwork && activeNetwork
      ? [
          {
            key: 'network',
            label: t('menu.network.show'),
            icon: FaNetworkWired,
            to: generatePath(NetworksRoutes.Dashboard, {
              slug: activeNetwork.slug,
            }),
          },
        ]
      : isCircle && activeCircle
        ? [
            {
              key: 'community',
              label: t('menu.community.space'),
              icon: FaEllo,
              to: generatePath(CommunitiesRoutes.Dashboard, {
                slug: activeCircle.slug,
              }),
            },
          ]
        : [
            {
              key: 'account',
              label: t('menu.my.account'),
              icon: FaIdCard,
              to: UserRoutes.MyProfile,
            },
          ]

  const switchState = useUserMenuContextSwitchState({
    circles,
    networks,
    user,
  })

  const wrapWithClose = (fn?: () => void) => () => {
    fn?.()
    onClose?.()
  }

  const contextSwitchShared = {
    user,
    circles,
    networks,
    contextIri,
    loading,
    circlesLoading,
    hoveredCircleId,
    setHoveredCircleId,
    handleSwitchContext,
    t,
    onClose,
  }

  if (asDrawer) {
    return (
      <VStack align="stretch" gap={0}>
        <UserMenuDrawerPrimaryNav
          rowPx={rowPx}
          rowPy={rowPy}
          rowGap={rowGap}
          user={user}
          captureEvent={captureEvent}
          t={t}
          rail={rail}
          endImpersonation={endImpersonation}
          isImpersonating={isImpersonating}
          onClose={onClose}
          wrapWithClose={wrapWithClose}
        />
        <Separator my={2} />
        {!circlesLoading && (circles.length > 0 || networks.length > 0) && (
          <ContextSwitchSection
            mode="drawer"
            rowPx={rowPx}
            rowPy={rowPy}
            switchState={switchState}
            {...contextSwitchShared}
          />
        )}
      </VStack>
    )
  }

  return (
    <>
      <UserMenuDropdownPrimaryNav
        rowPx={rowPx}
        rowPy={rowPy}
        rowGap={rowGap}
        user={user}
        captureEvent={captureEvent}
        t={t}
        endImpersonation={endImpersonation}
        isImpersonating={isImpersonating}
      />

      <MenuSeparator />

      {!circlesLoading && (circles.length > 0 || networks.length > 0) && (
        <ContextSwitchSection
          mode="dropdown"
          rowPx={rowPx}
          rowPy={rowPy}
          switchState={switchState}
          {...contextSwitchShared}
        />
      )}
    </>
  )
}
